/**
 * What the Map tab asks for and what comes back — `docs/property-maps-v2.md`
 * §4.
 *
 * One file of types and one thin function per route, so no screen builds a
 * path by hand. The shapes mirror the server's rows rather than the database's
 * columns: `areaSqft` is already a number here (the server casts `numeric` to
 * `float8`, because `pg` hands a bare `numeric` back as a string and a string
 * does not add up).
 */
import type { Audience, FeatureKind, Glyph, LayerColor, MapBase } from '@rowted/shared'
import { request } from '../lib/api.js'

export interface MapSummary {
  id: string
  name: string
  base: MapBase
  archived: boolean
  updatedAt: string
  layerCount: number
  shapeCount: number
  /** `audienceLabel` already applied, in `sort_order`, at most three. */
  audienceSummary: string[]
}

export interface MeasurementPart {
  id: string
  name: string
  value: number
  source: string
  featureId: string | null
  mapChanged: boolean
  /** Its shape was deleted: the number stays and the row says so (§4.1). */
  shapeDeleted: boolean
  measuredOn: string | null
  measuredByName: string | null
}

export interface MeasurementGroup {
  typeId: string
  type: string
  unit: string
  total: number
  source: string
  parts: MeasurementPart[]
}

export interface MapTabPayload {
  hasLocation: boolean
  /** The property's pin, which is where a new map's camera starts. */
  location: { lng: number; lat: number } | null
  maps: MapSummary[]
  measurementsSummary: MeasurementGroup[]
  /** Facts about the company, not about this person (§5.1 state 5, §5.2). */
  features: { maps: boolean; measurements: boolean }
}

export interface MapLayer {
  id: string
  mapId: string
  name: string
  color: LayerColor
  audience: Audience
  divisionIds: string[]
  ownerUserId: string
  pinnedToTickets: boolean
  crewCanAdd: boolean
  hiddenOnTickets: boolean
  sortOrder: number
  version: number
}

export interface MapFeature {
  id: string
  layerId: string
  kind: FeatureKind
  geometry: unknown
  shape: unknown
  name: string | null
  note: string | null
  color: LayerColor | null
  glyph: Glyph | null
  photoFileId: string | null
  areaSqft: number | null
  lengthFt: number | null
  source: 'office' | 'crew_app'
  version: number
}

export interface OneMapPayload {
  map: {
    id: string
    propertyId: string
    name: string
    base: MapBase
    view: { center: [number, number]; zoom: number } | null
    sortOrder: number
    archived: boolean
    version: number
  }
  propertyId: string
  layers: MapLayer[]
  features: MapFeature[]
  /** For `audienceLabel`, which prints names and never ids. */
  divisions: { id: string; name: string; colour: string }[]
}

export const mapTab = (propertyId: string): Promise<MapTabPayload> =>
  request<MapTabPayload>(`/property-maps/property/${propertyId}`)

export const oneMap = (mapId: string): Promise<OneMapPayload> =>
  request<OneMapPayload>(`/property-maps/maps/${mapId}`)

export const createMap = (
  propertyId: string,
  body: { name: string; from?: 'blank' | { copyMapId: string }; firstLayer?: boolean },
): Promise<{ id: string }> => request<{ id: string }>(
  `/property-maps/property/${propertyId}/maps`, { method: 'POST', body })

/**
 * Which map this property was last looked at on (§5.1 state 3).
 *
 * `localStorage`, wrapped, because a browser that refuses it is a browser that
 * still has to open the first map rather than nothing.
 */
const LAST = (propertyId: string) => `rowted.map.last.${propertyId}`

export function rememberLastMap(propertyId: string, mapId: string): void {
  try { window.localStorage.setItem(LAST(propertyId), mapId) } catch { /* private window */ }
}

export function lastMapOf(propertyId: string): string | null {
  try { return window.localStorage.getItem(LAST(propertyId)) } catch { return null }
}

// ---------------------------------------------------------------------------
// Writes (§4.1, §4.2)
// ---------------------------------------------------------------------------

export interface LayerInput {
  name: string
  color?: LayerColor
  audience?: Audience
  divisionIds?: string[]
  pinnedToTickets?: boolean
}

export const addLayer = (mapId: string, body: LayerInput): Promise<{ id: string }> =>
  request<{ id: string }>(`/property-maps/maps/${mapId}/layers`, { method: 'POST', body })

export const patchLayer = (
  id: string, body: Partial<LayerInput> & {
    crewCanAdd?: boolean; hiddenOnTickets?: boolean; sortOrder?: number; version: number
  },
): Promise<MapLayer> => request<MapLayer>(`/property-maps/layers/${id}`, { method: 'PATCH', body })

export const duplicateLayer = (id: string): Promise<{ id: string }> =>
  request<{ id: string }>(`/property-maps/layers/${id}/duplicate`, { method: 'POST' })

export const deleteLayer = (id: string, moveTo?: string | null): Promise<void> =>
  request<void>(
    `/property-maps/layers/${id}${moveTo ? `?moveTo=${moveTo}` : ''}`, { method: 'DELETE' })

export const setMapAudience = (
  mapId: string, body: { audience: Audience; divisionIds?: string[] },
): Promise<{ changed: { id: string; name: string }[] }> =>
  request(`/property-maps/maps/${mapId}/audience`, { method: 'POST', body })

export interface FeatureBody {
  kind: FeatureKind
  geometry: unknown
  shape?: unknown
  name?: string
  note?: string | null
  color?: LayerColor | null
  glyph?: Glyph | null
  photoFileId?: string | null
}

export const addFeature = (layerId: string, body: FeatureBody): Promise<MapFeature> =>
  request<MapFeature>(`/property-maps/layers/${layerId}/features`, { method: 'POST', body })

export const patchFeature = (
  id: string, body: Partial<FeatureBody> & { layerId?: string; version: number },
): Promise<MapFeature> =>
  request<MapFeature>(`/property-maps/features/${id}`, { method: 'PATCH', body })

export const deleteFeature = (id: string): Promise<void> =>
  request<void>(`/property-maps/features/${id}`, { method: 'DELETE' })

export const patchMap = (
  id: string,
  body: { name?: string; base?: MapBase; view?: unknown; archived?: boolean; version: number },
): Promise<{ id: string; version: number }> =>
  request(`/property-maps/maps/${id}`, { method: 'PATCH', body })

/** Who a choice of audience would reach today (§5.5's *Who sees it* card). */
export const audiencePreview = (
  layerId: string, audience: Audience, divisionIds: string[],
): Promise<{ crews: { id: string; name: string; divisionName: string | null }[] }> =>
  request(`/property-maps/layers/${layerId}/audience-preview`
    + `?audience=${audience}&divisionIds=${divisionIds.join(',')}`)

// ---------------------------------------------------------------------------
// Measurements (§4.3)
// ---------------------------------------------------------------------------

/** One row the review dialog is about to write. */
export interface PartBody {
  name: string
  typeId: string
  value: number
  source: 'walked' | 'aerial' | 'typed' | 'map_layer'
  featureId?: string | null
  measuredOn?: string | null
  note?: string | null
}

/** A name already taken, by index into what was sent (§4.3). */
export interface Clash { index: number; existingId: string }

export const measurementsOf = (propertyId: string): Promise<{ groups: MeasurementGroup[] }> =>
  request(`/property-maps/property/${propertyId}/measurements`)

/**
 * Write parts. **Nothing is written when a name clashes and `onClash` is
 * `ask`** — the whole set is refused with a 409 naming which rows, which is
 * what lets the dialog ask before anything moves (§4.3).
 */
export const saveParts = (
  propertyId: string,
  body: { parts: PartBody[]; onClash?: 'ask' | 'replace' | 'both' },
): Promise<{ saved: number }> =>
  request(`/property-maps/property/${propertyId}/measurements`, { method: 'POST', body })

export const patchPart = (
  id: string, body: { name?: string; typeId?: string; value?: number },
): Promise<void> => request(`/property-maps/parts/${id}`, { method: 'PATCH', body })

export const deletePart = (id: string): Promise<void> =>
  request(`/property-maps/parts/${id}`, { method: 'DELETE' })

/** **Update** on a *map changed* row: take the shape's number again. */
export const refreshPart = (id: string): Promise<{ value: number }> =>
  request(`/property-maps/parts/${id}/refresh`, { method: 'POST' })

export interface KeepShape {
  kind: FeatureKind
  geometry: unknown
  shape?: unknown
  name?: string | null
  typeId?: string | null
  typeName?: string | null
}

/** **Keep this drawing as a map** — only the shapes move (§5.4). */
export const keepAsMap = (
  propertyId: string,
  body: { name: string; layerMode: 'one' | 'per_type'; shapes: KeepShape[] },
): Promise<{ id: string }> =>
  request(`/property-maps/property/${propertyId}/keep-as-map`, { method: 'POST', body })

/**
 * The measurement types this company has, and what is already measured here.
 *
 * The same `GET /measurements/:propertyId` the Overview card reads, because
 * the Type picker's question — "which types exist" — is the one that endpoint
 * already answers, and a second list of types is a second answer.
 */
export interface MeasurementType {
  measurementTypeId: string
  name: string
  unit: string
  value: string | null
  partCount: number
}

export const measurementTypes = (propertyId: string): Promise<{ items: MeasurementType[] }> =>
  request(`/measurements/${propertyId}`)
